import React, { useEffect, useState } from "react";
import { Drawer, Input, Table, Button, message, Popconfirm, Typography, Row, Col, Grid } from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import PackingQRParser from "./PackingQRParser";
import { fetchPackingDetail, updatePackingItems } from "../packingThunk";

const { Text } = Typography;

const validateItems = (items) => {
    if (!items || items.length === 0) {
        return { valid: false, message: "Add at least one product" };
    }

    const seen = new Set();
    for (const item of items) {
        const key = `${item.gtin}::${item.serial_number}`;
        if (seen.has(key)) {
            return { valid: false, message: `Duplicate serial number found: ${item.serial_number}` };
        }
        seen.add(key);
    }

    return { valid: true };
};

const PackingEditDrawer = ({ open, packingId, onClose, onUpdated }) => {
    const dispatch = useDispatch();
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ssccCode, setSsccCode] = useState("");
    const [items, setItems] = useState([]);

    useEffect(() => {
        if (!open || !packingId) return;

        setLoading(true);
        dispatch(fetchPackingDetail(packingId))
            .unwrap()
            .then((data) => {
                setSsccCode(data.sscc_code);
                setItems(
                    (data.items || []).map((it) => ({
                        manufacture_id: it.manufacture_id,
                        manufacturer_name: it.manufacturer_name,
                        gtin: it.gtin,
                        serial_number: it.serial_number,
                        batch_name: it.batch_name,
                        batch_expiry: it.batch_expiry_raw,
                        raw_qr_code: it.raw_qr_code,
                    }))
                );
            })
            .catch((err) => message.error(err || "Failed to load packing"))
            .finally(() => setLoading(false));
    }, [open, packingId, dispatch]);

    const handleAddItem = (item) => {
        setItems((prev) => [...prev, item]);
    };

    const handleRemoveItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        const validation = validateItems(items);
        if (!validation.valid) {
            message.error(validation.message);
            return;
        }

        setSaving(true);
        try {
            const result = await dispatch(
                updatePackingItems({
                    id: packingId,
                    items: items.map((it) => ({
                        manufacture_id: it.manufacture_id,
                        gtin: it.gtin,
                        serial_number: it.serial_number,
                        batch_name: it.batch_name,
                        batch_expiry: it.batch_expiry,
                        raw_qr_code: it.raw_qr_code,
                    })),
                })
            ).unwrap();

            message.success(result.message || "Packing updated successfully");
            onUpdated?.();
            onClose();
        } catch (err) {
            message.error(err || "Failed to update packing");
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { title: "Manufacturer", dataIndex: "manufacturer_name", key: "manufacturer_name" },
        { title: "GTIN", dataIndex: "gtin", key: "gtin" },
        { title: "Serial Number", dataIndex: "serial_number", key: "serial_number" },
        { title: "Batch Name", dataIndex: "batch_name", key: "batch_name" },
        { title: "Batch Expiry", dataIndex: "batch_expiry", key: "batch_expiry" },
        {
            title: "Action",
            key: "action",
            render: (_, __, index) => (
                <Popconfirm title="Remove this product?" onConfirm={() => handleRemoveItem(index)}>
                    <Button danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Drawer
            title="Edit Packing"
            open={open}
            onClose={onClose}
            width={720}
            destroyOnClose
            styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Text strong>SSCC Code</Text>
                    <Input value={ssccCode} disabled style={{ marginTop: 5 }} />
                </Col>

                <Col span={24}>
                    <PackingQRParser onAdd={handleAddItem} />
                </Col>

                <Col span={24}>
                    <Table
                        rowKey={(_, index) => index}
                        columns={columns}
                        dataSource={items}
                        loading={loading}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: "No products added yet" }}
                    />
                </Col>

                <Col span={24}>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSave}
                    >
                        Save Changes
                    </Button>
                </Col>
            </Row>
        </Drawer>
    );
};

export default PackingEditDrawer;
